import { type Locale, defaultLocale, getLocaleConfig, isValidLocale } from "@/config/locale";
import { normalizeReferenceFxCurrencyCode } from "@/lib/geo/geo-pref";

/**
 * ISO 3166-1 alpha-2 → reference display currency (ISO 4217).
 * EU non-euro members and non-EU regions. Eurozone is handled via `EUROZONE` set.
 */
const REFERENCE_CURRENCY_BY_COUNTRY: Record<string, string> = {
	// EU — non-euro
	PL: "PLN",
	SE: "SEK",
	DK: "DKK",
	CZ: "CZK",
	HU: "HUF",
	RO: "RON",
	// Non-EU — majors & frequent visitors
	US: "USD",
	GB: "GBP",
	UK: "GBP",
	CH: "CHF",
	LI: "CHF",
	NO: "NOK",
	IS: "ISK",
	CA: "CAD",
	AU: "AUD",
	SG: "SGD",
	KR: "KRW",
	IN: "INR",
	BR: "BRL",
	MX: "MXN",
	ZA: "ZAR",
	TR: "TRY",
	IL: "ILS",
	NZ: "NZD",
};

/** EU member states using EUR (incl. HR since 2023 and BG since 2026). */
const EUROZONE = new Set([
	"AT",
	"BE",
	"BG",
	"HR",
	"CY",
	"EE",
	"FI",
	"FR",
	"DE",
	"GR",
	"IE",
	"IT",
	"LV",
	"LT",
	"LU",
	"MT",
	"NL",
	"PT",
	"SK",
	"SI",
	"ES",
]);

function referenceFromCountry(countryCode: string | null | undefined): string | null {
	if (!countryCode) return null;
	const cc = countryCode.toUpperCase();
	if (EUROZONE.has(cc)) return "EUR";
	return REFERENCE_CURRENCY_BY_COUNTRY[cc] ?? null;
}

/**
 * When `CF-IPCountry` is missing, infer reference currency from UI locale + channel.
 */
function referenceFromLocale(locale: Locale, channel: string): string {
	const ch = channel.toLowerCase();
	if (ch === "us") {
		const usHints: Partial<Record<Locale, string>> = {
			bg: "EUR",
			pl: "PLN",
			sv: "SEK",
			da: "DKK",
			cs: "CZK",
			hu: "HUF",
			ro: "RON",
			de: "EUR",
			fr: "EUR",
			it: "EUR",
			es: "EUR",
			nl: "EUR",
			pt: "EUR",
			// Greift nur ohne CF-IPCountry — mit Ländersignal gewinnt ohnehin BR → BRL.
			// Die Locale allein sagt hier aber schon Brasilien, also nicht auf USD fallen.
			"pt-br": "BRL",
			// Wie pt-br: die Locale allein sagt Mexiko, also Pesos statt der USD-Kanalwährung.
			"es-mx": "MXN",
			el: "EUR",
			fi: "EUR",
			en: "USD",
		};
		return usHints[locale] ?? "USD";
	}

	const euHints: Partial<Record<Locale, string>> = {
		bg: "EUR",
		pl: "PLN",
		sv: "SEK",
		da: "DKK",
		cs: "CZK",
		hu: "HUF",
		ro: "RON",
		en: "EUR",
		de: "EUR",
		fr: "EUR",
		it: "EUR",
		es: "EUR",
		nl: "EUR",
		pt: "EUR",
		"pt-br": "BRL",
		"es-mx": "MXN",
		el: "EUR",
		fi: "EUR",
	};
	return euHints[locale] ?? getLocaleConfig(locale).fallbackCurrency;
}

/**
 * Resolve the visitor's DISPLAY (reference) currency. Display-only: the
 * result may differ from the channel settlement currency (EUR/USD) and must
 * never be used to charge or to build Saleor mutation variables.
 *
 * Precedence (first match wins):
 * 1. `referenceCurrencyOverride` (geo_pref_v2 cookie) — only if it
 *    normalizes via `normalizeReferenceFxCurrencyCode`.
 * 2. `countryCode` (CF-IPCountry): Eurozone members → "EUR", else
 *    REFERENCE_CURRENCY_BY_COUNTRY; unknown countries fall through.
 * 3. Locale+channel hint table: on `us` channel `en` → "USD"; on `eu`
 *    unknown locales → the locale's `fallbackCurrency`.
 * Always returns a code (never null). Invalid `locale` → defaultLocale.
 */
export function getReferenceCurrencyForContext(params: {
	countryCode: string | null | undefined;
	locale: string;
	channel: string;
	/** User preference from `geo_pref` — overrides geo/locale inference when valid */
	referenceCurrencyOverride?: string | null;
}): string {
	const override = normalizeReferenceFxCurrencyCode(params.referenceCurrencyOverride);
	if (override) {
		return override;
	}

	const { countryCode, locale, channel } = params;
	const loc: Locale = isValidLocale(locale) ? locale : defaultLocale;

	const fromCountry = referenceFromCountry(countryCode);
	if (fromCountry) {
		return fromCountry;
	}

	return referenceFromLocale(loc, channel);
}

/**
 * Reference currencies whose display needs a visible settlement line on the PDP and a
 * permanent settlement notice in the cart, because their symbol can pass for the
 * channel currency. Wave 1: MXN only.
 */
const VISIBLE_SETTLEMENT_HINT_CURRENCIES: ReadonlySet<string> = new Set(["MXN"]);

/** Whether a reference display currency must always be paired with the settlement currency. */
export function requiresVisibleSettlementHint(referenceCurrency: string): boolean {
	return VISIBLE_SETTLEMENT_HINT_CURRENCIES.has(referenceCurrency.toUpperCase());
}
